import { useState } from "react";
import { useTranslation } from "react-i18next";
import { MAIN_SITE_URL } from "@/constants/site";

export default function ClosingSections() {
  const { t } = useTranslation();
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  const faqItems = t("faq.items", { returnObjects: true }) as {
    q: string;
    a: string;
  }[];
  const safetyLinks = t("safety.links", { returnObjects: true }) as string[];

  return (
    <>
      {/* Safety */}
      <section className="bg-background-50">
        <div className="mx-auto w-full max-w-4xl px-4 py-14 md:px-6 md:py-16">
          <div className="rounded-2xl border border-accent-200 bg-accent-50 p-6 md:p-8">
            <div className="flex items-start gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-accent-500 text-background-50">
                <i className="ri-alert-line text-lg leading-none" />
              </span>
              <div>
                <h2 className="text-lg font-bold text-foreground-950 md:text-xl">
                  {t("safety.title")}
                </h2>
                <p className="mt-2 text-sm leading-relaxed text-foreground-700">{t("safety.text")}</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {safetyLinks.map((link) => (
                    <span
                      key={link}
                      className="whitespace-nowrap rounded-full border border-accent-300/60 bg-background-50 px-3 py-1 text-xs font-medium text-foreground-600"
                    >
                      {link}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-background-100">
        <div className="mx-auto w-full max-w-3xl px-4 py-14 md:px-6 md:py-20">
          <h2 className="text-center text-2xl font-bold tracking-tight text-foreground-950 md:text-3xl">
            {t("faq.title")}
          </h2>
          <div className="mt-8 space-y-3">
            {faqItems.map((item, i) => {
              const open = openFaq === i;
              return (
                <div
                  key={item.q}
                  className="overflow-hidden rounded-lg border border-background-200 bg-background-50"
                >
                  <button
                    type="button"
                    onClick={() => setOpenFaq(open ? null : i)}
                    className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
                  >
                    <span className="text-sm font-semibold text-foreground-950 md:text-base">
                      {item.q}
                    </span>
                    <i
                      className={`ri-add-line flex h-5 w-5 shrink-0 items-center justify-center text-foreground-500 transition-transform ${
                        open ? "rotate-45" : ""
                      }`}
                    />
                  </button>
                  {open && (
                    <div className="border-t border-background-200 px-5 py-4">
                      <p className="text-sm leading-relaxed text-foreground-600">{item.a}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-primary-500">
        <div className="mx-auto w-full max-w-4xl px-4 py-16 text-center md:px-6 md:py-20">
          <h2 className="text-2xl font-bold tracking-tight text-background-50 md:text-4xl">
            {t("finalCta.title")}
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-sm text-primary-100 md:text-base">
            {t("finalCta.subtitle")}
          </p>
          <a
            href={MAIN_SITE_URL}
            target="_blank"
            rel="noopener nofollow"
            className="mt-8 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-background-50 px-7 py-3 text-sm font-semibold text-primary-700 transition-colors hover:bg-background-100"
          >
            {t("finalCta.button")}
            <i className="ri-arrow-right-line flex h-4 w-4 items-center justify-center text-base leading-none" />
          </a>
        </div>
      </section>
    </>
  );
}